/**
 * Surface content — 3.7 Test (UAT & regressions).
 *
 * ZERO literals in the component (build brief §0a): every figure, name, id,
 * date and sentence the surface renders is declared here or read from
 * `@/demo/data`.
 *
 * CANONICAL STATE (build brief §0): Van Dijk's phase is BUILD (3 of 7) at NOW,
 * so this surface shows test PREPARATION, not a live UAT:
 *  - The REGRESSION figures are honest during Build — the suites are generated
 *    from the approved requirements and run nightly against the build. The
 *    deck's numbers are transcribed onto them: 614 cases from 142 requirements,
 *    96% requirement coverage (6 untested), 571 passing (31 fail · 12 blocked).
 *    They are derived from the suite rows below rather than typed twice, so the
 *    KPI row and the table can never disagree.
 *  - UAT itself has NOT started. The deck's "38% key-user progress · 9 testers ·
 *    5 days in" is therefore rendered as the PLAN — a window that opens on the
 *    date the client has not yet confirmed — never as a live reading.
 *
 * DATE MATCHING (two facts this surface shares with sibling surfaces; both are
 * derived here the same way they are derived there, and both carry a comment so
 * a change moves them together):
 *  - UAT start = go-live − 42 days ⇒ 1 Oct. Keep matched with
 *    `content/portal.ts` (UAT_LEAD_DAYS / UAT_START).
 *  - "Confirm UAT dates with client" was due 25 Aug ⇒ 2 days late at NOW. Keep
 *    matched with `content/projectOverview.ts` (HUMAN_TODOS · todo-uat-dates)
 *    and `content/portal.ts` (CLIENT_OWED · uat-window). It is one fact seen
 *    from three sides, never three facts.
 */

import { NOW, VAN_DIJK, agentById, heroProject, personById, thinSpace } from '@/demo/data'
import type { PhaseKey } from '@/demo/data'
import type { StatusTone } from '@/components/px/StatusPill'

const PROJECT = heroProject()
const REQUIREMENTS = VAN_DIJK.requirements
const AUTHOR = agentById('test-author')
const QA_OWNER = personById('blerta-hoxha')

/* ── Date arithmetic (against NOW / go-live — never Date.now()) ─────────── */

const MS_DAY = 86_400_000
const ZONE = 'Europe/Amsterdam'
const shortDate = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: ZONE })

/** Project dates are day-precision; anchor them at the NOW time-of-day. */
const atProjectHour = (isoDay: string): Date => new Date(`${isoDay}T09:00:00+02:00`)
const shiftDays = (from: Date, days: number): Date => new Date(from.getTime() + days * MS_DAY)
const startOfDay = (d: Date): number => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())

/** Whole days from NOW to an ISO day; negative once the date is past. */
const daysFromNow = (isoDay: string): number =>
  Math.round((startOfDay(atProjectHour(isoDay)) - startOfDay(NOW)) / MS_DAY)

const plural = (n: number, unit: string): string => `${n} ${unit}${n === 1 ? '' : 's'}`

/* ── Chrome ─────────────────────────────────────────────────────────────── */

export const TEST_PAGE = {
  moduleLabel: 'PX · Project',
  contextPill: 'TEST · PREP',
  /** BUILD, not test — the overview, the migration surface and this page all
   *  describe one world, and the Test gate has not opened. */
  phase: 'build' as PhaseKey,
  title: 'Test — UAT & regressions',
  standfirst:
    'Every case is written from a requirement, so coverage is traceable back to what the client asked for. ' +
    'The regression suites already run nightly against the build; the key users test the same board when the ' +
    'client confirms the window.',
  phaseNote:
    'Test prep runs inside Build — the suites are generated as the modules land. The Test gate opens when the ' +
    'UAT window is confirmed.',
  traceLinkLabel: 'Requirements register',
  migrationLinkLabel: 'Migration',
} as const

/* ── Anchors (every affordance on this page lands on one of these) ──────── */

export const ANCHORS = {
  suites: 'suites',
  defects: 'defects',
  untested: 'untested',
  uatWindow: 'uat-window',
} as const

/* ── Real routes this surface links to ──────────────────────────────────── */

export const HREFS = {
  requirements: `/project/${PROJECT.id}/requirements`,
  migration: `/project/${PROJECT.id}/migration`,
  portal: '/portal',
  retailTemplate: '/library/templates/retail-rollout',
  testAuthor: `/library/agents/${AUTHOR.id}`,
  qaOwner: `/resources/people/${QA_OWNER.id}`,
} as const

/* ── Suites ─────────────────────────────────────────────────────────────── */

export type SuiteOwner =
  | { kind: 'person'; personId: string; name: string; meta: string; href: string }
  | { kind: 'agent'; agentId: string; name: string; meta: string; href: string }

const QA_OWNER_REF: SuiteOwner = {
  kind: 'person',
  personId: QA_OWNER.id,
  name: QA_OWNER.name,
  meta: `${QA_OWNER.role} · ${QA_OWNER.office}`,
  href: HREFS.qaOwner,
}

const AUTHOR_REF: SuiteOwner = {
  kind: 'agent',
  agentId: AUTHOR.id,
  name: AUTHOR.name,
  meta: `${AUTHOR.runs} · ${AUTHOR.reviewGate}`,
  href: HREFS.testAuthor,
}

export interface Suite {
  key: string
  name: string
  /** The register epic these requirements sit in — the register's vocabulary,
   *  mirrored here (keep matched with EPICS in `content/requirementsRegister.ts`). */
  epic: string
  /** Requirement ids the suite covers — a code range, set in mono. */
  rangeFrom: number
  rangeTo: number
  cases: number
  fail: number
  blocked: number
  /** Requirements in the range that have at least one case. */
  covered: number
  /** Requirements in the range with no case yet — the ones listed under #untested. */
  untested: number
  owner: SuiteOwner
  /** Why a row carries weight; null on a row that is simply passing. */
  note: string | null
}

/**
 * Six suites, and they are the whole regression estate: their cases sum to the
 * deck's 614, their failures to 31, their blocked cases to 12, and their
 * covered requirements to 136 of 142 (⇒ 96%). Nothing here is a sample.
 */
export const SUITES: readonly Suite[] = [
  {
    key: 'promotions',
    name: 'Promotions',
    epic: 'Promotions',
    rangeFrom: 49,
    rangeTo: 78,
    cases: 143,
    fail: 7,
    blocked: 4,
    covered: 30,
    untested: 0,
    owner: QA_OWNER_REF,
    note: 'All seven failures are price-rule failures on overlapping promotions — the decision behind them is still open on the register.',
  },
  {
    key: 'loyalty',
    name: 'Loyalty',
    epic: 'Loyalty',
    rangeFrom: 79,
    rangeTo: 96,
    cases: 76,
    fail: 6,
    blocked: 3,
    covered: 16,
    untested: 2,
    owner: AUTHOR_REF,
    note: 'Three cases are blocked on the loyalty import — the source export is missing two columns.',
  },
  {
    key: 'inventory',
    name: 'Inventory',
    epic: 'Inventory',
    rangeFrom: 97,
    rangeTo: 124,
    cases: 111,
    fail: 6,
    blocked: 2,
    covered: 27,
    untested: 1,
    owner: QA_OWNER_REF,
    note: null,
  },
  {
    key: 'pricing',
    name: 'Pricing',
    epic: 'Pricing',
    rangeFrom: 31,
    rangeTo: 48,
    cases: 118,
    fail: 5,
    blocked: 0,
    covered: 18,
    untested: 0,
    owner: QA_OWNER_REF,
    note: null,
  },
  {
    key: 'reporting',
    name: 'Reporting & exports',
    epic: 'Cross-epic',
    rangeFrom: 125,
    rangeTo: 142,
    cases: 74,
    fail: 4,
    blocked: 1,
    covered: 16,
    untested: 2,
    owner: AUTHOR_REF,
    note: 'Reporting lines belong to the epic they report on, so this suite spans several.',
  },
  {
    key: 'store-hierarchy',
    name: 'Store hierarchy',
    epic: 'Store hierarchy',
    rangeFrom: 1,
    rangeTo: 30,
    cases: 92,
    fail: 3,
    blocked: 2,
    covered: 29,
    untested: 1,
    owner: AUTHOR_REF,
    note: null,
  },
]

/** Cases neither failing nor blocked — passing is derived, never asserted. */
export const suitePass = (suite: Suite): number => suite.cases - suite.fail - suite.blocked

/** `#31–#48` — the register range, rendered in mono. */
export const rangeLabel = (suite: Suite): string => `#${suite.rangeFrom}–#${suite.rangeTo}`

/** A failing row this heavy is read before anything else on the page. */
export const HEAVY_FAIL_THRESHOLD = 7

export const isHeavyFail = (suite: Suite): boolean => suite.fail >= HEAVY_FAIL_THRESHOLD

export interface SuiteTotals {
  cases: number
  pass: number
  fail: number
  blocked: number
  covered: number
  untested: number
  coveragePct: number
}

export const suiteTotals = (suites: readonly Suite[]): SuiteTotals => {
  const cases = suites.reduce((n, s) => n + s.cases, 0)
  const fail = suites.reduce((n, s) => n + s.fail, 0)
  const blocked = suites.reduce((n, s) => n + s.blocked, 0)
  const covered = suites.reduce((n, s) => n + s.covered, 0)
  const untested = suites.reduce((n, s) => n + s.untested, 0)
  return {
    cases,
    fail,
    blocked,
    pass: cases - fail - blocked,
    covered,
    untested,
    coveragePct: Math.round((covered / REQUIREMENTS.total) * 100),
  }
}

export const TOTALS: SuiteTotals = suiteTotals(SUITES)

/** KPI row labels — the four figures the surface leads with. */
export const KPI_LABELS = {
  cases: 'Cases',
  coverage: 'Requirement coverage',
  passing: 'Regression passing',
} as const

export const SUITE_TABLE = {
  label: 'Regression suites',
  heading: 'Suites and the requirements they cover',
  caption:
    'Generated from the approved requirements, run nightly against the build. Each range opens the register, ' +
    'where every requirement carries the moment in a workshop where it was said.',
  columns: {
    suite: 'Suite',
    requirements: 'Requirements covered',
    cases: 'Cases',
    pass: 'Pass',
    fail: 'Fail',
    owner: 'Owner',
  },
  /** Screen-reader wording for the two figure columns that carry weight. */
  failLabel: (n: number): string => plural(n, 'failing case'),
  blockedLabel: (n: number): string => `${n} blocked`,
  untestedLabel: (n: number): string => `${plural(n, 'requirement')} untested`,
  agentOwnerNote: 'Agent-owned suites still land in a person’s review queue before a result is quoted to the client.',
  qaOwnerNote: `${QA_OWNER.name}’s allocation record sits on another rollout — the resource view is the authority on her calendar, this table on who owns the suite.`,
  rangeLinkTitle: 'Open these requirements on the register',
} as const

/* ── Overnight run (the same run the home digest and 3.1 both show) ─────── */

const PRICING = SUITES.find((s) => s.key === 'pricing')
const PROMOTIONS = SUITES.find((s) => s.key === 'promotions')
if (!PRICING || !PROMOTIONS) throw new Error('Suite seed is missing pricing/promotions')

/** Deck + `content/home.ts` + `content/projectOverview.ts`: 61 cases, 03:45. */
const NIGHTLY_CASES = 61

export const NIGHTLY_RUN = {
  panelLabel: 'Overnight · what the test author did',
  tone: 'done' as StatusTone,
  pillLabel: 'Done',
  text: `${NIGHTLY_CASES} regression cases generated from requirements #${PRICING.rangeFrom}–#${PROMOTIONS.rangeTo}`,
  time: '03:45',
  /** The run's log lives on this surface — the suites it wrote into. */
  runHref: `#${ANCHORS.suites}`,
  agentName: AUTHOR.name,
  agentHref: HREFS.testAuthor,
  agentLine: `${AUTHOR.produces} · ${AUTHOR.runs} · ${AUTHOR.reviewGate} · ${AUTHOR.acceptancePct}% of its cases accepted as written.`,
  landedLine: `Last night’s ${NIGHTLY_CASES} cases landed in the ${PRICING.name.toLowerCase()} and ${PROMOTIONS.name.toLowerCase()} suites below.`,
} as const

/* ── Open defects, by cause (the deck's taxonomy) ───────────────────────── */

export interface DefectCause {
  key: string
  label: string
  count: number
  prose: string
  href: string | null
  linkLabel: string | null
}

/** Sums to the 31 failing cases above — the panel is the same 31, re-read. */
export const DEFECT_CAUSES: readonly DefectCause[] = [
  {
    key: 'ambiguous',
    label: 'Requirement was ambiguous',
    count: 11,
    prose:
      'The case and the build read the same line differently. Nothing is broken — a decision is missing, and it belongs on the register.',
    href: HREFS.requirements,
    linkLabel: 'back to the register',
  },
  {
    key: 'config',
    label: 'Config error',
    count: 9,
    prose: 'A setting on the module rather than the code. Corrected in the build and re-run the same night.',
    href: null,
    linkLabel: null,
  },
  {
    key: 'migration-data',
    label: 'Migration data',
    count: 7,
    prose:
      'The case is right and the build is right; the record it ran against arrived wrong. The fix is in the field map.',
    href: HREFS.migration,
    linkLabel: 'open the field map',
  },
  {
    key: 'bug',
    label: 'Genuine bug',
    count: 4,
    prose: 'The code does something nobody asked for. Four of thirty-one — the rest are decisions, settings and data.',
    href: null,
    linkLabel: null,
  },
]

export const defectTotal = (causes: readonly DefectCause[]): number =>
  causes.reduce((n, c) => n + c.count, 0)

export const DEFECTS_PANEL = {
  label: 'Open defects · by cause',
  heading: 'What the 31 failures actually are',
  lead: (total: number): string =>
    `Every failing case is classified before it is assigned, because most of them are not bugs. All ${total} are open.`,
  countUnit: (n: number): string => plural(n, 'defect'),
} as const

/* ── The 6 untested requirements ────────────────────────────────────────── */

export interface UntestedRequirement {
  key: string
  idLabel: string
  title: string
  epic: string
  reason: string
}

const untested = (id: number, title: string, epic: string, reason: string): UntestedRequirement => ({
  key: `req-${id}`,
  idLabel: `#${id}`,
  title,
  epic,
  reason,
})

export const UNTESTED: readonly UntestedRequirement[] = [
  untested(14, 'Store-group rollup for franchise branches', 'Store hierarchy', 'Open decision — which branches roll up to which group.'),
  untested(83, 'Points expiry on dormant accounts', 'Loyalty', 'Open decision — the expiry window is not agreed.'),
  untested(91, 'Points transfer between family cards', 'Loyalty', 'Open decision — waiting on the client’s privacy review.'),
  untested(108, 'Cycle-count tolerance per store size', 'Inventory', 'Open decision — the tolerance table has not been supplied.'),
  untested(131, 'Margin report by promotion', 'Promotions', 'Open decision — depends on which promotion wins on an overlap.'),
  untested(138, 'Weekly stock-value export', 'Inventory', 'Open decision — the export format is still under discussion.'),
]

export const UNTESTED_PANEL = {
  label: 'Untested requirements',
  heading: (n: number): string => `The ${n} requirements with no case`,
  lead: (n: number, total: number, open: number): string =>
    `${n} of ${total} requirements have no case yet. All ${n} sit among the ${open} still open — a case cannot be ` +
    `written against a decision nobody has made, so the coverage figure states the gap instead of hiding it.`,
  openRequirements: REQUIREMENTS.open,
  columns: { requirement: 'Requirement', epic: 'Epic', reason: 'Why there is no case' },
  registerLinkLabel: 'open the register',
} as const

/* ── UAT window · planned ───────────────────────────────────────────────── */

/** UAT opens six weeks before go-live — keep matched with `content/portal.ts`. */
const UAT_LEAD_DAYS = 42
const UAT_START_DATE = shiftDays(atProjectHour(VAN_DIJK.goLive.date), -UAT_LEAD_DAYS)

/** The same row 3.1 shows as "Confirm UAT dates with client", due 25 Aug. */
const UAT_DATES_DUE_ISO = '2026-08-25'
const UAT_DAYS_LATE = -daysFromNow(UAT_DATES_DUE_ISO)

/** Deck 3.7: nine key users test this rollout (mirrors portal's UAT_KEY_USERS). */
const UAT_KEY_USERS = 9

/**
 * The deck's "38% · 5 days in" is what this bar will read once the window runs.
 * At NOW it reads 0: no key user has opened the board, because the dates are
 * not confirmed. The plan is rendered as a plan.
 */
export const UAT = {
  startLabel: shortDate.format(UAT_START_DATE),
  weeksBeforeGoLive: UAT_LEAD_DAYS / 7,
  goLiveLabel: VAN_DIJK.goLive.label,
  keyUsers: UAT_KEY_USERS,
  daysLate: UAT_DAYS_LATE,
  lateLabel: `${plural(UAT_DAYS_LATE, 'day')} late`,
  dueLabel: `due ${shortDate.format(atProjectHour(UAT_DATES_DUE_ISO))}`,

  panelLabel: 'UAT window · planned',
  heading: 'The key-user window, as planned',
  blockingLine: `Blocked on the client: the UAT dates are ${plural(UAT_DAYS_LATE, 'day')} late. It is the same item ` +
    'the project overview and the client portal both carry — one fact, three views.',
  portalLinkLabel: 'the client’s open items',
  progressLabel: 'Key-user progress',
  progressPct: 0,
  progressReading: '0% — UAT has not started',
  progressNote:
    'The bar fills when a key user executes a case. Until the window is confirmed there is nothing to read, and a ' +
    'planned percentage is not a result.',
  kpi: {
    label: 'UAT window',
    qualifier: `${UAT_KEY_USERS} key users · dates await client confirmation`,
  },
} as const

/** The three plan lines, with their values derived rather than typed. */
export const UAT_PLAN: readonly { key: string; label: string; value: string }[] = [
  {
    key: 'opens',
    label: 'Opens',
    value: `${UAT.startLabel} · ${UAT.weeksBeforeGoLive} weeks before go-live (${UAT.goLiveLabel})`,
  },
  { key: 'users', label: 'Key users', value: `${UAT_KEY_USERS} named by the client` },
  {
    key: 'board',
    label: 'Board',
    value: `The same board as the regressions — ${thinSpace(TOTALS.cases)} cases, filtered to the requirements each key user owns`,
  },
]

/* ── Back into the library (mint — what PX proposes) ────────────────────── */

const NEW_LIBRARY_CASES = 22

export const LIBRARY_FEEDBACK = {
  label: 'Back into the library',
  tag: 'Proposed by PX',
  /** Deck-verbatim, split so the pack name can carry its link. */
  headlineBefore: `The seven pricing failures produced ${NEW_LIBRARY_CASES} new regression cases attached to `,
  packLabel: 'retail pricing pack v4',
  packHref: HREFS.retailTemplate,
  headlineAfter: '.',
  prose:
    'A defect found on this project becomes a regression test on every project that installs the pack. The next ' +
    'rollout inherits the failure before it has a chance to repeat it — which is the whole reason the library exists.',
  gateLine: `${AUTHOR.name} · ${AUTHOR.reviewGate} — the pack owner accepts them into v4.`,
  countNote: `The ${NEW_LIBRARY_CASES} cases live in the pack, not in this project’s ${thinSpace(TOTALS.cases)} — they run here ` +
    'again the next time the pack is installed.',
  failuresLinkLabel: 'the seven failures',
  failuresHref: `#${ANCHORS.suites}`,
} as const

/* ── Density note ───────────────────────────────────────────────────────── */

export const densityNote = (suites: readonly Suite[], totals: SuiteTotals): string =>
  `The table shows ${suites.length} suites rolled up from ${thinSpace(totals.cases)} cases — case-level detail is ` +
  `withheld, so each suite links to its requirement range on the register rather than opening a list this ` +
  `prototype does not hold. The pass/fail figures are REGRESSION runs against the build, not UAT: the key-user ` +
  `board appears when the client confirms the window, and the progress bar reads 0% until then. ` +
  `${totals.untested} requirements have no case at all, and they are named above rather than rounded away.`
